/**
 * transform-speakers block
 *
 * "Who's in the room" section: left-aligned heading + subhead, then a grid of
 * speaker cards — portrait (or a placeholder box), name with company logo,
 * role, and the speaker's session title.
 *
 * The lineup used to be a full-bleed horizontal rail that ran off the right
 * edge of the screen and was dragged or swiped along. It is a plain wrapping
 * grid now: every announced speaker is visible at once, nothing is hidden
 * behind a gesture, and the section stays inside the same centered container
 * (`max-w-screen-xl` + page gutter) as the heading above it, so no card
 * overhangs the page margin.
 *
 * Columns by breakpoint — 2 on phones, 3 on tablets, 4 from `lg` (1040px) up.
 * With today's eighteen speakers that is four full rows of four on desktop
 * plus a fifth row holding the last two (see the note in the content file). Cards are sized by the grid
 * rather than by a fixed width, so every photo is the same size at a given
 * breakpoint and the lineup can grow by whole rows without any of this needing
 * to be retuned.
 *
 * Because the grid derives the card width, the photos are considerably smaller
 * than the rail's were — about 290px at the desktop container width, against
 * 364-520px before — which is what lets two rows fit without the section
 * getting tall. It also brings the 320x400 source images much closer to the box
 * they render in (see the note in the content file).
 *
 * The company logo sits on the name line rather than the title line, so the
 * lineup can be read as a row of brands at a glance without the (longer,
 * wrapping) titles moving it around. It is decorative by default: every title
 * here already names the company in text, so an alt would just repeat it to a
 * screen reader — pass `logo_alt` only for a speaker whose title does not.
 *
 * Logos live in `/images/c1-transform-2026/logos/` as 128x128 tiles that all
 * share one shape and one optical mark size; see the note in the content file
 * before adding another.
 *
 * ## The session slot
 *
 * The session title used to live in a teal panel that appeared over the
 * portrait on hover. Hover is a desktop-only, pointer-only affordance: on a
 * phone the titles were simply unreachable, and even on a desktop a visitor
 * had to discover that the photos respond to a cursor at all. So every card
 * now ends in a session slot that is always on screen — the titles are half
 * the reason the lineup is on the page, and nothing about them should be
 * conditional on input device.
 *
 * Three details make the slot read as a designed row rather than as text
 * dumped under a photo:
 *
 *  - It is fenced off by a hairline (`#DCDADA`, the stroke the boxed cards,
 *    the FAQ and the Featured sessions rows on this page already use) so the
 *    session reads as its own field, not as a third line of the caption.
 *  - The role above it reserves two lines (`min-h-[3em]`). Roles here are one
 *    or two lines depending on how long the company name is, and without the
 *    reserve every hairline in a row sits at a different height. With it, the
 *    hairlines and the eyebrows below them line up straight across a desktop
 *    row, which is most of what makes the grid look deliberate.
 *  - The title is set in the display face and in `teal-dark`, against the
 *    role's Public Sans grey. Colour and typeface — not size — carry the
 *    hierarchy, so the session can stay physically small in a ~290px card and
 *    still read as the more important of the two lines.
 *
 * The eyebrow's pink square is the one place the brand pink appears here. It is
 * decorative (`aria-hidden`), which is what lets it be `pink-accent`: that pink
 * is a light tint built for dark surfaces and would fail contrast as text on
 * this cream page, so the eyebrow's own words are `teal-dark` and the pink is
 * carried by a 7px block instead.
 *
 * A speaker with no announced `session` gets the same slot with the pink square
 * hollowed out and `pending_session_label` in grey — while a lineup is still
 * filling in, an
 * announced and a pending session reading as two states of one field is more
 * honest than the pending cards ending in a ragged gap. Set
 * `pending_session_label: ""` in frontmatter to drop the slot from those cards
 * entirely.
 *
 * Between those two sits `role`. The hosts open the lineup and are on the
 * programme in a stated capacity ("Keynote speaker", "Moderator") before their
 * own sessions are announced, which is neither a session title nor a gap. A
 * `role` fills the slot in the announced typography — display face, teal-dark —
 * but drops the eyebrow entirely: "Session" would be wrong over "Moderator",
 * and naming the field over its own value ("Role: Moderator") states the
 * obvious. The hairline alone fences the slot, and the phrase says what it is.
 * `session` wins if a card somehow carries both. The field name matches the
 * `role` on `transform-agenda` speakers, which labels the moderator of a
 * panel.
 *
 * Styled to match the C1 Transform page (Anybody display heading, Public Sans
 * subhead, sharp corners).
 */
export function TransformSpeakers({
  heading,
  subhead,
  speakers,
  placeholder_color = "#F7F7F5",
  pending_session_label = "To be announced",
  note,
}: {
  heading?: string;
  subhead?: string;
  speakers?: Array<{
    name?: string;
    title?: string;
    image?: string;
    image_alt?: string;
    /** Square company logo tile shown beside the name. */
    logo?: string;
    /** Alt text for the logo. Defaults to decorative — the title already
     *  names the company — so only set this when it does not. */
    logo_alt?: string;
    /** Announced session title, shown in the card's session slot. */
    session?: string;
    /** The speaker's stated capacity at the event ("Keynote speaker",
     *  "Moderator"), shown in the slot under a "Role" eyebrow when no
     *  `session` is announced. Styled as announced, not pending. */
    role?: string;
  }>;
  /** Background color of a speaker photo placeholder (when no image). */
  placeholder_color?: string;
  /** Shown in the session slot of a speaker with no `session` yet. Set to an
   *  empty string to give those cards no session slot at all. */
  pending_session_label?: string;
  /** Optional text shown in the next speaker slot, in line with the photos
   *  (e.g. "More speakers to be announced."). */
  note?: string;
}) {
  return (
    <div className="py-16 md:py-20 lg:py-24">
      <div className="mx-auto max-w-screen-xl px-4 md:px-8">
        {heading && (
          <h2
            className="font-display text-[50.5px] font-semibold leading-[1.1] text-charcoal"
            dangerouslySetInnerHTML={{ __html: heading }}
          />
        )}
        {subhead && (
          <p className="mt-5 font-body text-xl font-medium text-charcoal">
            {subhead}
          </p>
        )}

        {((speakers && speakers.length > 0) || note) && (
          /* The row gap is larger than the column gap on purpose: a name, a
             role and a session sit under each photo, so equal gaps read as a
             card being closer to the one below it than to the one beside it. */
          <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-8 md:mt-12 md:grid-cols-3 md:gap-x-5 md:gap-y-10 lg:grid-cols-4 lg:gap-x-6">
            {speakers?.map((s, i) => {
              /* The logo is glued to the last word of the name (see below), so
                 split it off here. A one-word name leaves `nameHead` empty and
                 the whole thing rides in the no-wrap span. */
              const nameWords = (s.name || "").split(" ");
              const nameTail = nameWords.pop() || "";
              const nameHead = nameWords.join(" ");
              /* An announced session, a stated role, or pending — all three
                 render the slot; only an empty `pending_session_label`
                 suppresses it on a pending card. A session and a role are both
                 confirmed, so they share one styling and differ only in the
                 eyebrow that names which of the two the line is. */
              const slotValue = s.session || s.role;
              /* A role reads as a standalone phrase, so it takes the slot
                 without the eyebrow above it. */
              const isRole = Boolean(!s.session && s.role);
              const showSessionSlot = Boolean(slotValue || pending_session_label);
              return (
              <div key={i} className="flex flex-col">
                <div
                  className="group/photo relative aspect-[4/5] w-full overflow-hidden"
                  style={s.image ? undefined : { backgroundColor: placeholder_color }}
                  aria-hidden={s.image ? undefined : true}
                >
                  {s.image && (
                    /* The slow, small scale on hover is the only thing left of
                       the old hover panel: it acknowledges the cursor without
                       putting anything behind it, now that the session title is
                       never hidden. `will-change` is deliberately absent — nine
                       promoted layers cost more than the effect is worth. */
                    <img
                      src={s.image}
                      alt={s.image_alt || s.name || ""}
                      className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover/photo:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover/photo:scale-100"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="mt-4 md:mt-5">
                  {(s.name || s.logo) && (
                    /* The logo runs inline with the name rather than as a flex
                       sibling of it, and inside a no-wrap span with the name's
                       last word. Names do wrap in the ~170px cards of the
                       2-column phone layout, and both of the other arrangements
                       break there: a flexed logo floats out beside the whole
                       two-line block with a gap of dead space before it, and a
                       plain inline one can be pushed onto a line of its own
                       under the name. Tied to the last word it can only ever
                       land immediately after the name — "Karl / McGuinness▣". */
                    <p className="font-display text-base font-bold leading-tight text-charcoal md:text-19">
                      {nameHead && `${nameHead} `}
                      <span className="whitespace-nowrap">
                        {nameTail}
                        {s.logo && (
                          /* Width and height are set on the element as well as
                             in CSS so a lazy tile reserves its box and the line
                             does not reflow when it arrives. */
                          <img
                            src={s.logo}
                            alt={s.logo_alt || ""}
                            aria-hidden={s.logo_alt ? undefined : true}
                            width={28}
                            height={28}
                            className="ml-2 inline-block h-6 w-6 align-middle md:h-7 md:w-7"
                            loading="lazy"
                          />
                        )}
                      </span>
                    </p>
                  )}
                  {s.title && (
                    /* Two lines are reserved whether or not this role needs
                       them, so the session hairlines below line up across a
                       row. `3em` is two lines at this element's own leading, so
                       it tracks the font size across breakpoints on its own. A
                       role long enough to need a third line simply pushes the
                       slot down on that one card. */
                    <p className="mt-1 min-h-[3em] font-body text-sm text-gray-80 md:text-base">
                      {s.title}
                    </p>
                  )}
                </div>
                {showSessionSlot && (
                  <div className="mt-3 border-t border-[#DCDADA] pt-3.5">
                    {!isRole && (
                      <p
                        className={`flex items-center gap-[7px] font-body text-[10.5px] font-bold uppercase leading-none tracking-[0.13em] ${
                          slotValue ? "text-teal-dark" : "text-gray-80"
                        }`}
                      >
                        {/* Filled pink when the slot carries a confirmed session
                            or role, hollow grey when it is still pending, so the
                            states of the slot are legible at a glance and not
                            only by reading the line below. */}
                        <span
                          aria-hidden="true"
                          className={`h-[7px] w-[7px] shrink-0 ${
                            slotValue ? "bg-pink-accent" : "border border-[#B9B7B6]"
                          }`}
                        />
                        Session
                      </p>
                    )}
                    {slotValue ? (
                      /* `text-balance` rather than `pretty`: at this measure a
                         title runs to two or three lines on most cards, and
                         evening them out reads far tidier in a grid of this
                         size than leaving one word stranded on a last line. */
                      <p
                        className={`font-display text-[15px] font-semibold leading-[1.35] tracking-[-0.01em] text-balance text-teal-dark md:text-[17px] ${
                          isRole ? "" : "mt-2"
                        }`}
                      >
                        {slotValue}
                      </p>
                    ) : (
                      /* Grey rather than a faded teal: this line has to clear
                         4.5:1 on cream like any other body copy, and teal-dark
                         thinned enough to read as pending does not. */
                      <p className="mt-2 font-body text-sm leading-[1.35] text-gray-80">
                        {pending_session_label}
                      </p>
                    )}
                  </div>
                )}
              </div>
              );
            })}
            {note && (
              /* Takes a cell of its own, so it reads as the next speaker slot
                 rather than as a caption under the grid. Sized down a step from
                 the rail's copy because the cell it has to fit in is now
                 roughly half as wide.

                 Centred against the PHOTOS beside it, not against the whole
                 cell: the wrapper repeats a photo's `aspect-[4/5] w-full`, so
                 in an equal-width grid it is exactly as tall as the portraits
                 in its row and centring inside it puts the copy on their
                 midline. Centring in the cell itself would drop the copy by
                 half the caption block below the photos, and would shift again
                 whenever a role wraps to another line. (A grid cell can also be
                 taller than its content, which is why `self-center` alone would
                 not track the photos.) */
              <div className="flex aspect-[4/5] w-full items-center">
                <p className="text-left font-body text-lg font-medium leading-tight text-charcoal md:text-xl lg:text-2xl">
                  {note}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
